import { mimeTypes } from 'client/constants/editorSettings'
import { useCustomFonts } from 'client/context/CustomFontsContext'
import { DataFile } from 'client/store/files/filesReducer'
import { wrapFontFamily } from 'common/components/entities/Text/utils'
import {
  Font,
  getFontDataWithActualFontLabel,
  getFontPropertiesFromFontFiles,
  validateRawCustomFontFile,
} from 'common/utils/fontsUtils'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Upload01Icon } from 'src/editor/shared/icons/upload-01-icon'
import { TertiaryButton } from 'src/editor/shared/ui-lib/buttons'
import {
  OptionSingleValueType,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'
import { FileManager } from 'src/editor/shared/ui-lib/uploader/file-manager'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'

export const getFontFamilyOption = (font: Font) => ({
  value: font.id,
  label: font.fontLabel,
})

function CustomFontPicker({
  update,
  fontFamily,
}: {
  update: (customFontFamily: OptionSingleValueType<number>) => void
  fontFamily?: string
}) {
  const {
    userFonts,
    setUserFonts,
    setUserFontFiles,
    userFontFiles,
    isFetching,
    loadFonts,
  } = useCustomFonts()
  const [fileManagerOpen, setFileManagerOpen] = useState(false)
  const { t } = useTypedTranslation()

  useEffect(() => {
    loadFonts()
  }, [])

  const getCustomFontFamilyOption = (fontFamily?: string) => {
    if (fontFamily) {
      const activeFontFamily = userFonts.find(
        el => wrapFontFamily(el.fullName) === fontFamily,
      )
      if (activeFontFamily) {
        return getFontFamilyOption(activeFontFamily)
      }
    }
    return null
  }

  function afterRemove(fileId: number) {
    const updatedUserFontsList = userFonts.filter(
      userFont => userFont.id !== fileId,
    )
    setUserFonts(updatedUserFontsList)
  }

  function updateFontFile(file: DataFile) {
    const userFontFile = userFonts.find(userFont => userFont.id === file.id)
    if (userFontFile) {
      update(getFontFamilyOption(userFontFile))
    }
  }

  async function afterFileUpload(file: DataFile) {
    const fontFileMeta = await getFontPropertiesFromFontFiles([file])
    const fontData = getFontDataWithActualFontLabel(fontFileMeta[0], userFonts)
    const updatedUserFontsList = [...userFonts, fontData]
    const uploadedUserFontFiles = {
      ...userFontFiles,
      [file.id]: file,
    }
    setUserFonts(updatedUserFontsList)
    setUserFontFiles(uploadedUserFontFiles)
  }

  function getCustomFileName(file: DataFile) {
    const userFontFile = userFonts.find(userFont => userFont.id === file.id)
    if (userFontFile) {
      return userFontFile.fontLabel
    }
    return ''
  }

  const validateUploadFile = async (file: File, mimeType: string) => {
    const validationResult = await validateRawCustomFontFile(file, mimeType)
    if (!validationResult) {
      toast.error(t('components.custom_font_picker.singe_file_parse_failed'))
    }
    return validationResult
  }

  return (
    <div className="mt-3 flex flex-col gap-3">
      <div className="flex gap-3">
        <SelectNew
          onChange={update}
          selectedOption={getCustomFontFamilyOption(fontFamily)}
          options={
            userFonts
              ? userFonts.map((el: Font) => getFontFamilyOption(el))
              : []
          }
          isMulti={false}
          isLoading={isFetching}
          wrapperClassName="!m-0 p-0 flex-1"
        />
        <TertiaryButton
          onClick={() => setFileManagerOpen(true)}
          disabled={isFetching}
          iconBefore={<Upload01Icon />}
        >
          {t('components.custom_font_picker.upload_button')}
        </TertiaryButton>
      </div>
      {fileManagerOpen && (
        <FileManager
          closeFileManager={() => setFileManagerOpen(false)}
          mimeTypes={mimeTypes.font}
          fileType={'font'}
          onInsert={updateFontFile}
          getCustomFileName={getCustomFileName}
          afterRemove={afterRemove}
          validateUploadFile={validateUploadFile}
          afterFileUpload={afterFileUpload}
        />
      )}
    </div>
  )
}

export default CustomFontPicker
